import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, isValid, parseISO } from 'date-fns';
import { enGB, uk as ukLocale } from 'date-fns/locale';
import { Tooltip } from 'react-tooltip';

import UntypedModal from '../../components/Modal';
import { Paginator as UntypedPaginator } from '../../components/Paginator/Paginator';
import { Loader } from '../../components/Loader/Loader';
import websocketConnection from '../../websocket';
import { ReactComponent as DeleteIcon } from '../../assets/delete-new.svg';
import { ReactComponent as RegenerateIcon } from './assets/regenerate.svg';
import {
  socialGraphApi,
  SavedGraphDTO,
  SavedGraphListDTO,
  parseApiDate,
  toApiDate,
} from '../../utils/socialGraphApi';
import GenerateGraphModal, {
  GenerateGraphValues,
} from '../../components/GenerateGraphModal/GenerateGraphModal';
import { showRequestError } from '../../utils/requestError';
import { cutString } from '../../utils/cutString';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { trackSocialGraphEvent } from '../SocialGraphPOC/analytics';
import styles from './GraphsPage.module.scss';

const Modal = UntypedModal as unknown as React.FC<{
  isVisible: boolean;
  title: string;
  content: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
}>;

const Paginator = UntypedPaginator as unknown as React.FC<{
  size: number;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}>;

const PAGE_SIZE = 50;
const NAME_MAX_LENGTH = 65;

// API values → translation keys, resolved through t() at render time.
const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  generating: 'Generating',
  ready: 'Ready',
  failed: 'Failed',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  actor_group: 'Actor group',
  actor: 'Actor',
  case: 'Case',
  topic: 'Topic',
  threat: 'Threat',
};

// status text colors per the Figma design (green Ready / red Failed)
const STATUS_CLASSES: Record<string, string> = {
  ready: styles.statusReady,
  failed: styles.statusFailed,
  generating: styles.statusGenerating,
  queued: styles.statusGenerating,
};

const UK_LANGUAGES = ['uk', 'ua', 'ua-UK', 'ru', 'ru-RU'];

// Backend timestamps are naive UTC (same convention as StoryCard) — append Z
// unless the string already carries timezone info.
function formatCreatedAt(iso: string, language: string): string {
  if (!iso) return '';
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const date = parseISO(hasTimezone ? iso : `${iso}Z`);
  if (!isValid(date)) return iso;
  const isUk = UK_LANGUAGES.includes(language);
  return format(date, isUk ? 'd MMM yyyy' : 'MMM d, yyyy', {
    locale: isUk ? ukLocale : enGB,
  });
}

// analysis-window dates are plain yyyy-MM-dd days — same dd/MM/yy display as
// the Generate Graph modal's datepicker
function formatWindowDate(value?: string | null): string | null {
  const date = parseApiDate(value);
  return date && isValid(date) ? format(date, 'dd/MM/yy') : null;
}

export function GraphsPage() {
  const { t, i18n } = useTranslation();
  const [graphs, setGraphs] = useState<SavedGraphListDTO | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isActive: boolean;
    id: number | null;
  }>({ isActive: false, id: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [regeneratingIds, setRegeneratingIds] = useState<number[]>([]);
  // graph whose regenerate modal (name + optional window) is open
  const [regenerateModalGraph, setRegenerateModalGraph] = useState<SavedGraphDTO | null>(null);
  const [cancellingIds, setCancellingIds] = useState<number[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // the whole feature is behind the social_graph flag; flag-less requests get a
  // 403 that API.fetch treats as an expired session (token wipe + redirect), so
  // nothing may be fetched until the flag is confirmed
  const [currentUser] = useCurrentUser();
  const hasSocialGraphFlag = Boolean(
    currentUser?.workspace?.config?.featureflags?.includes('social_graph'),
  );
  // garbage like ?page=abc / ?page=0 falls back to the first page
  const requestedPage = Number(searchParams.get('page') ?? 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;

  const fetchGraphs = useCallback(
    (signal?: AbortSignal) => {
      // guarded here, not just at the mount effect, so retry and post-mutation
      // refreshes can't fire either if the flag flips at runtime
      if (!hasSocialGraphFlag) return;
      setGraphs(null);
      setLoadFailed(false);
      socialGraphApi
        .listGraphs({ page, size: PAGE_SIZE }, signal)
        .then((data) => setGraphs(data))
        .catch((error) => {
          showRequestError(error, t('Graphs'));
          // aborts come from pagination/unmount — a newer request owns the state
          if ((error as Error)?.name !== 'AbortError') setLoadFailed(true);
        });
    },
    [page, t, hasSocialGraphFlag],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchGraphs(controller.signal);
    return () => controller.abort();
  }, [fetchGraphs]);

  // Worker-side status transitions (queued → generating → ready/failed) are pushed
  // over the WS channel — patch the matching row so the list updates live.
  useEffect(() => {
    const handleUpdate = (message: { data?: Partial<SavedGraphDTO> }) => {
      const data = message?.data;
      if (!data?.id) return;
      setGraphs((prev) =>
        prev
          ? {
              ...prev,
              objects: prev.objects.map((item) =>
                item.id === data.id ? { ...item, ...data } : item,
              ),
            }
          : prev,
      );
    };
    websocketConnection.addHandler('SOCIAL_GRAPH', handleUpdate);
    return () => websocketConnection.removeHandler('SOCIAL_GRAPH', handleUpdate);
  }, []);

  const handleDelete = (id: number) => {
    setIsDeleting(true);
    socialGraphApi
      .deleteGraph(id)
      .then(() => {
        trackSocialGraphEvent('sg_graph_deleted', { graph_id: id }, currentUser);
        fetchGraphs();
      })
      .catch((error) => showRequestError(error, t('Graphs')))
      .finally(() => {
        setIsDeleting(false);
        setDeleteConfirmModal({ isActive: false, id: null });
      });
  };

  const handleRegenerate = (graph: SavedGraphDTO, values: GenerateGraphValues) => {
    const id = graph.id;
    setRegeneratingIds((prev) => [...prev, id]);
    const patchRow = (updated: SavedGraphDTO) =>
      setGraphs((prev) =>
        prev
          ? {
              ...prev,
              objects: prev.objects.map((item) => (item.id === updated.id ? updated : item)),
            }
          : prev,
      );
    // one call: a non-blank name renames in the same request, and explicit date
    // fields replace the analysis window (nulls clear it back to all time).
    // Date fields only for actor graphs — a window on a case graph is a 400.
    const name = values.name.trim();
    socialGraphApi
      .regenerateGraph(id, {
        ...(graph.kind === 'actor'
          ? {
              start_date: toApiDate(values.startDate) ?? null,
              end_date: toApiDate(values.endDate) ?? null,
            }
          : {}),
        ...(name && name !== graph.name ? { name } : {}),
      })
      .then((updated) => {
        patchRow(updated);
        trackSocialGraphEvent(
          'sg_graph_generate_requested',
          {
            mode: 'regenerate',
            graph_id: graph.id,
            graph_kind: graph.kind,
            source_type: graph.source_type,
            renamed: Boolean(name && name !== graph.name),
            has_date_range: Boolean(values.startDate || values.endDate),
            from: 'graphs_list',
          },
          currentUser,
        );
      })
      .catch((error) => showRequestError(error, t('Graphs')))
      .finally(() => setRegeneratingIds((prev) => prev.filter((item) => item !== id)));
  };

  const handleCancel = (graph: SavedGraphDTO) => {
    if (!graph.task_id) return;
    setCancellingIds((prev) => [...prev, graph.id]);
    // no graph-level cancel endpoint — revoke the task via the DELETE task
    // endpoints, picked by the graph's kind. A cancelled first run deletes the
    // graph, a cancelled regeneration reverts it to ready — refetch to learn which.
    const cancel =
      graph.kind === 'actor'
        ? socialGraphApi.cancelActors(graph.task_id)
        : socialGraphApi.cancelCase(graph.task_id);
    cancel
      .then(() => {
        trackSocialGraphEvent(
          'sg_graph_generation_cancelled',
          { graph_id: graph.id, graph_kind: graph.kind, source_type: graph.source_type, from: 'graphs_list' },
          currentUser,
        );
        fetchGraphs();
      })
      .catch((error) => showRequestError(error, t('Graphs')))
      .finally(() => setCancellingIds((prev) => prev.filter((item) => item !== graph.id)));
  };

  // direct-URL access without the flag: bounce home (the sidebar link is
  // already hidden by the same check in Layout)
  if (currentUser && !hasSocialGraphFlag) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="list-content">
      <div className="page-header">
        <div className="breadcrumb">
          <span>{t('Graphs')}</span>
        </div>
      </div>

      {loadFailed ? (
        <div className={styles.emptyCard}>
          <p className={styles.emptyTitle}>{t('Failed to load graphs')}</p>
          <button type="button" className="btn-primary" onClick={() => fetchGraphs()}>
            {t('Try again')}
          </button>
        </div>
      ) : !graphs ? (
        <Loader />
      ) : graphs.objects?.length > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.graphs}>
            <thead>
              <tr>
                <th scope="col">{t('Name')}</th>
                <th scope="col">{t('Source type')}</th>
                <th scope="col">{t('Status')}</th>
                <th scope="col">{t('Date range')}</th>
                <th scope="col">{t('Created')}</th>
                <th scope="col">
                  <span className={styles.visuallyHidden}>{t('Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {graphs.objects.map((graph) => {
                const status = graph.status?.toLowerCase();
                const isGenerating =
                  status === 'generating' ||
                  status === 'queued' ||
                  regeneratingIds.includes(graph.id);
                // only actor-kind graphs have an analysis window; a window-less
                // actor graph covers the whole period
                const rangeStart = formatWindowDate(graph.start_date);
                const rangeEnd = formatWindowDate(graph.end_date);
                const dateRange =
                  rangeStart || rangeEnd
                    ? `${rangeStart ?? '…'} – ${rangeEnd ?? '…'}`
                    : graph.kind === 'actor'
                      ? t('All time')
                      : '—';

                return (
                  <tr key={graph.id}>
                    <td className={styles.nameCell}>
                      <Link to={`/graphs/${graph.id}`} className={styles.nameLink}>
                        {cutString(graph.name, NAME_MAX_LENGTH)}
                      </Link>
                      <div className={styles.nameSubInfo}>
                        <span>#{graph.id}</span>
                        {graph.user && (graph.user.first_name || graph.user.last_name) ? (
                          <>
                            <span>&#8226;</span>
                            <span className={styles.createdBy}>
                              {t('created by1 ')}
                              <span className={styles.userName}>
                                {[graph.user.first_name, graph.user.last_name]
                                  .filter(Boolean)
                                  .join(' ')}
                              </span>
                            </span>
                          </>
                        ) : null}
                      </div>
                    </td>
                    <td className={styles.typeCell}>
                      {graph.source_type
                        ? t(SOURCE_TYPE_LABELS[graph.source_type] ?? graph.source_type)
                        : '—'}
                    </td>
                    <td className={`${styles.statusCell} ${STATUS_CLASSES[status] ?? ''}`}>
                      {t(STATUS_LABELS[status] ?? graph.status)}
                    </td>
                    <td className={styles.rangeCell}>{dateRange}</td>
                    <td className={styles.createdCell}>
                      {formatCreatedAt(graph.created_at, i18n.language)}
                    </td>
                    <td>
                      <div className={styles.controls}>
                        {isGenerating ? (
                          <button
                            type="button"
                            className={styles.cancelBtn}
                            disabled={cancellingIds.includes(graph.id)}
                            onClick={() => handleCancel(graph)}
                          >
                            {t('Cancel1')}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              data-tooltip-id="regenerate-graph"
                              data-tooltip-content={t('Regenerate')}
                              aria-label={t('Regenerate')}
                              onClick={() => setRegenerateModalGraph(graph)}
                            >
                              <RegenerateIcon width={20} height={20} />
                            </button>

                            <button
                              type="button"
                              className={styles.actionBtn}
                              data-tooltip-id="delete-graph"
                              data-tooltip-content={t('Delete')}
                              aria-label={t('Delete')}
                              onClick={() => setDeleteConfirmModal({ isActive: true, id: graph.id })}
                            >
                              <DeleteIcon />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              <Tooltip id="regenerate-graph" place="bottom" style={{ zIndex: '300' }} />
              <Tooltip id="delete-graph" place="bottom" style={{ zIndex: '300' }} />
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.emptyCard}>
          <p className={styles.emptyTitle}>{t('No graphs yet')}</p>
          <p className={styles.emptyDescription}>
            {t('You can generate a graph from a case, topic, actor group, or actor page.')}
          </p>
        </div>
      )}

      {graphs && (
        <Paginator
          size={graphs.size}
          page={graphs.page}
          total={graphs.total}
          onPageChange={(newPage) => setSearchParams({ page: String(newPage) })}
        />
      )}

      <Modal
        isVisible={deleteConfirmModal.isActive}
        title={t('Delete')}
        content={<p>{t('Are you sure you want to delete this graph?')}</p>}
        footer={
          <>
            <button
              className="btn-danger"
              onClick={() => deleteConfirmModal.id !== null && handleDelete(deleteConfirmModal.id)}
              disabled={isDeleting}
            >
              {t('Delete')}
            </button>
            <button
              className="btn-reset"
              onClick={() => setDeleteConfirmModal({ isActive: false, id: null })}
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => setDeleteConfirmModal({ isActive: false, id: null })}
      />

      {regenerateModalGraph && (
        <GenerateGraphModal
          mode="regenerate"
          // only actor-kind graphs have an analysis window
          showDateRange={regenerateModalGraph.kind === 'actor'}
          initialName={regenerateModalGraph.name}
          initialStartDate={parseApiDate(regenerateModalGraph.start_date)}
          initialEndDate={parseApiDate(regenerateModalGraph.end_date)}
          onClose={() => setRegenerateModalGraph(null)}
          onSubmit={(values) => {
            const graph = regenerateModalGraph;
            setRegenerateModalGraph(null);
            handleRegenerate(graph, values);
          }}
        />
      )}
    </div>
  );
}
